import type { MedplumClient } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';
import { getTrackerStage } from '@unify/fhir-model/resources/service-request';
import { TRACKER_STAGE_TAG_SYSTEM } from '@unify/fhir-model/constants';
import { therapyServiceTypeLabel } from '../../../lib/fhirValueSets/sessionAndEpisodeTypes';
import { extractServiceTypeCode } from '../../../lib/serviceRequestFhirMapping';
import {
  hasServiceRequestCategory,
  readTrackerStage,
} from '../../../lib/serviceRequestFhirConstants';

export type PendingServiceRequestCard = {
  id: string;
  serviceTypeLabel: string;
  stageCode: string;
  stageLabel: string;
  stageTone: 'info' | 'warning' | 'success' | 'neutral';
  submittedAt?: string;
  nextStepText: string;
  isOnHold: boolean;
};

/** Client-visible stages still in progress. */
export const CLIENT_PORTAL_PENDING_STAGE_CODES = new Set([
  'new-client',
  'insurance-eligibility',
  'needs-verification',
  'screening',
  'matching',
  'consultation',
  'inactive',
]);

const CLIENT_PORTAL_STAGE_LABELS: Record<string, string> = {
  'new-client': 'Request received',
  'insurance-eligibility': 'Insurance review',
  'needs-verification': 'Insurance review',
  screening: 'Clinical screening',
  matching: 'Provider matching',
  consultation: 'Consultation scheduling',
  inactive: 'On hold',
};

const CLIENT_PORTAL_NEXT_STEP_TEXT: Record<string, string> = {
  'new-client': "We've received your request and will review it soon.",
  'insurance-eligibility': "We're reviewing insurance and eligibility details.",
  'needs-verification': "We're reviewing insurance and eligibility details.",
  screening: "We're reviewing your care needs.",
  matching: "We're matching you with an appropriate provider.",
  consultation: "We're preparing the next scheduling step.",
  inactive: 'This request is currently on hold. Contact support if you have questions.',
};

const DEFAULT_SERVICE_TYPE_LABEL = 'Care service request';
const DEFAULT_STAGE_LABEL = 'In progress';
const DEFAULT_NEXT_STEP = 'Your care team is working on your request.';

/** Read tracker-stage tag code from ServiceRequest.meta.tag (includes legacy / alias codes). */
export function extractTrackerStageFromServiceRequest(
  sr: Pick<ServiceRequest, 'meta'>,
): string | undefined {
  const fromModel = getTrackerStage(sr as ServiceRequest);
  if (fromModel) return fromModel;
  const fromConstants = readTrackerStage(sr.meta);
  if (fromConstants) return fromConstants;
  return sr.meta?.tag?.find((t) => t.system === TRACKER_STAGE_TAG_SYSTEM)?.code;
}

export function isClientPortalPendingStage(stage: string | undefined): stage is string {
  return typeof stage === 'string' && CLIENT_PORTAL_PENDING_STAGE_CODES.has(stage);
}

export function isClientPortalServiceRequest(sr: ServiceRequest): boolean {
  return hasServiceRequestCategory(sr);
}

export function getClientPortalStageLabel(stageCode: string): string {
  return CLIENT_PORTAL_STAGE_LABELS[stageCode] ?? DEFAULT_STAGE_LABEL;
}

export function getClientPortalNextStepText(stageCode: string): string {
  return CLIENT_PORTAL_NEXT_STEP_TEXT[stageCode] ?? DEFAULT_NEXT_STEP;
}

export function getClientPortalStageTone(
  stageCode: string,
): PendingServiceRequestCard['stageTone'] {
  if (stageCode === 'inactive') return 'warning';
  if (stageCode === 'new-client') return 'neutral';
  return 'info';
}

function resolveServiceTypeLabel(sr: ServiceRequest): string {
  const code = extractServiceTypeCode(sr);
  if (code) {
    const label = therapyServiceTypeLabel(code);
    if (label && label !== code) return label;
    if (label) return label;
  }
  const text = sr.code?.text?.trim();
  if (text) return text;
  const display = sr.code?.coding?.[0]?.display?.trim();
  if (display) return display;
  return DEFAULT_SERVICE_TYPE_LABEL;
}

/** Map a ServiceRequest to a client card, or null when it should not appear in the portal. */
export function normalizePendingServiceRequest(
  sr: ServiceRequest,
): PendingServiceRequestCard | null {
  if (!sr.id) return null;
  if (!isClientPortalServiceRequest(sr)) return null;

  const stageCode = extractTrackerStageFromServiceRequest(sr);
  if (!isClientPortalPendingStage(stageCode)) return null;

  return {
    id: sr.id,
    serviceTypeLabel: resolveServiceTypeLabel(sr),
    stageCode,
    stageLabel: getClientPortalStageLabel(stageCode),
    stageTone: getClientPortalStageTone(stageCode),
    submittedAt: sr.authoredOn,
    nextStepText: getClientPortalNextStepText(stageCode),
    isOnHold: stageCode === 'inactive',
  };
}

function submittedAtSortKey(iso: string | undefined): number {
  if (!iso) return Number.NEGATIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
}

function toPatientSubjectRef(patientId: string): string {
  const trimmed = patientId.trim();
  return trimmed.startsWith('Patient/') ? trimmed : `Patient/${trimmed}`;
}

/**
 * Query pending client service requests for a portal patient.
 * Uses conservative search params (subject only) and filters client-side so
 * AccessPolicy/compartment rules are less likely to reject token/sort combos.
 */
export async function fetchPendingServiceRequestsForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<PendingServiceRequestCard[]> {
  const subject = toPatientSubjectRef(patientId);
  const attempts: Record<string, string>[] = [
    { subject, _sort: '-_lastUpdated', _count: '100' },
    { subject, _count: '100' },
  ];

  let lastError: unknown;
  for (const params of attempts) {
    try {
      const raw = await medplum.searchResources('ServiceRequest', params);
      if (!Array.isArray(raw)) {
        return [];
      }
      return filterAndSortPendingServiceRequests(raw);
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError ?? new Error('ServiceRequest search failed');
}

/** Defensively filter and sort pending client service requests for the portal. */
export function filterAndSortPendingServiceRequests(
  resources: ServiceRequest[],
): PendingServiceRequestCard[] {
  const cards: PendingServiceRequestCard[] = [];
  for (const sr of resources) {
    try {
      const card = normalizePendingServiceRequest(sr);
      if (card) cards.push(card);
    } catch {
      // Skip malformed resources — dashboard must not crash on bad data.
    }
  }
  return cards.sort(
    (a, b) => submittedAtSortKey(b.submittedAt) - submittedAtSortKey(a.submittedAt),
  );
}
